import {
  AutocompleteMenuSelector,
  ErrorChip,
  FormGroup,
  SelectInputWithImage,
  SelectOptionWithImage,
} from '@starsoft/common/components';
import {
  useDebounce,
  useKycMeByOriginIdWithOrigin,
  usePhoneNumberForm,
  useTranslation,
} from '@starsoft/common/hooks';
import { Country } from '@starsoft/common/models';
import { useCallback, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { UserPhoneNumberFormCreateProps } from '../PhoneNumberInput/props';
import {
  CreateDepositRequestDto,
  CreatePhoneNumberInputDto,
  UserPhoneNumberVerifyInputDto,
} from '@starsoft/common/dto';
import { KYCStatus } from '@starsoft/common/enums';
import { SettingsService } from '@starsoft/common/services/SettingsService';

/**
 * CountryCodeSelector Component
 * This component is responsible for rendering a country code selector with an autocomplete feature.
 * It manages the state of the selected country and handles asynchronous loading of country data.
 *
 * @param {UserPhoneNumberFormCreateProps} props - The properties for the component.
 * @param {boolean} props.isKycFlow - Indicates if the form is part of the KYC (Know Your Customer) flow.
 * @param {boolean} props.isContainerColor - Determines if the container should have a specific color.
 *
 * @returns {JSX.Element} The rendered country code selector component.
 *
 * @example
 * // Example usage:
 * <CountryCodeSelector isKycFlow={true} isContainerColor={false} />
 */
export default function CountryCodeSelector({
  isKycFlow,
  isContainerColor,
}: UserPhoneNumberFormCreateProps) {
  // Hook for translation in the 'account' namespace
  const { t } = useTranslation('account');
  // State to manage the search query for countries
  const [query, setQuery] = useState<string>('');
  // Access form context to manage form state
  const { watch, setValue } = useFormContext<
    CreatePhoneNumberInputDto &
      UserPhoneNumberVerifyInputDto &
      CreateDepositRequestDto
  >();
  // Debounce the query to reduce the frequency of updates
  const debouncedQuery = useDebounce(query);
  // Hook to get KYC status based on origin ID
  const { kyc } = useKycMeByOriginIdWithOrigin({
    enabled: isKycFlow,
  });
  // Determine if the phone number is verified based on KYC status
  const isVerified: boolean = isKycFlow && kyc?.status === KYCStatus.Verified;

  // Custom hook to manage phone number form state and actions
  const {
    country,
    countriesQuery: {
      fetchNextPage,
      error,
      refetch,
      countries,
      hasNextPage,
      isLoading,
      isFetchingNextPage,
    },
  } = usePhoneNumberForm({
    watch,
    setValue,
    countryQuery: debouncedQuery,
  });

  /**
   * setCountryId Function
   * This function sets the selected country ID and country object in the form state.
   *
   * @param {Country} country - The selected country object.
   *
   * @example
   * // Example usage:
   * setCountryId({ id: 1, name: 'United States', dialCode: '+1' });
   */
  function setCountryId(country: Country) {
    setValue('countryId', country.id?.toString(), {
      shouldDirty: true,
    });
    //@ts-ignore
    setValue('country', country);
  }

  /**
   * onSearchChange Function
   * This function updates the search query state when the user types in the search input.
   *
   * @param {string} value - The new search query value.
   *
   * @example
   * // Example usage:
   * onSearchChange('United');
   */
  const onSearchChange = useCallback((value: string) => {
    setQuery(value);
  }, []);

  // Callback to load more countries when the user scrolls to the end of the list
  const loadMore = useCallback(fetchNextPage, [fetchNextPage]);

  // Render an error chip if there's an error fetching countries
  if (error) {
    return <ErrorChip action={refetch} />;
  }

  // Render the form group with the autocomplete menu selector
  return (
    <FormGroup label={t('country') as string}>
      <AutocompleteMenuSelector
        options={countries ?? []}
        getOptionLabel={e => e?.name}
        getOptionValue={e => e?.id?.toString()}
        value={country?.id?.toString()}
        setValue={setCountryId}
        hasNextPage={hasNextPage}
        filterBy={['name']}
        onSearchChange={onSearchChange}
        isContainerColor={isContainerColor}
        title=""
        isAsync
        disabled={isVerified}
        loadMore={loadMore}
        isSmallerDrawer
        loading={isLoading}
        loadingMore={isFetchingNextPage}
        customComponents={{
          customOption: ({ handleSelect, option, selected }) => (
            <SelectOptionWithImage
              handleSelect={handleSelect}
              option={option}
              selected={selected}
              image={`${SettingsService.env.S3_BUCKET_BASE_URL}/flags/${option?.image}`}
              label={option?.name}
              description={option?.dialCode}
            />
          ),
          customInput: ({ handleOpen, open, disabled, option }) => (
            <SelectInputWithImage
              handleOpen={handleOpen}
              image={`${SettingsService.env.S3_BUCKET_BASE_URL}/flags/${option?.image}`}
              label={option?.dialCode}
              open={open}
              disabled={disabled}
              cardBg={isContainerColor}
            />
          ),
        }}
      />
    </FormGroup>
  );
}
